import {
  PropertyCartItem,
  AssetCartItem,
  EntityCartGroupResponse,
  EntityGroup,
  ServiceInfo,
} from '../types/cart.types';
import {
  initialPriceForService,
} from '../../service-selection/hooks/serviceHelpers';
import type {
  DisplayService,
  ProductService,
} from '../../../../types/order.types';

// ---------------------------------------------------------------------------
// Flatten grouped cart response into flat arrays
// ---------------------------------------------------------------------------

export const flattenGroupedCart = (
  groupedCart: Record<string, EntityCartGroupResponse>
) => {
  const propertyItems: PropertyCartItem[] = [];
  const assetItems: AssetCartItem[] = [];

  Object.values(groupedCart).forEach((entityGroup) => {
    Object.values(entityGroup.properties).forEach((propertyGroup) => {
      propertyGroup.propertyItems.forEach((item) => {
        propertyItems.push(item);
      });
      propertyGroup.assetOrders.forEach((item) => {
        assetItems.push(item);
      });
    });
  });

  return { propertyItems, assetItems };
};

// ---------------------------------------------------------------------------
// DisplayService → ServiceInfo conversion
// ---------------------------------------------------------------------------

/**
 * Convert a wizard-state DisplayService into a cart-state ServiceInfo.
 * Used when user-picked services in the cart slider need to flow back into
 * the cart's PropertyCartItem.services field. lineItemUUID is left absent —
 * the backend assigns it when the line is persisted.
 *
 * occupancyStatus is added dynamically to DisplayService at selection time
 * (see useServiceSelection.selectService) but isn't on the declared type;
 * the narrow assertion below picks it up when present, defaults to null.
 */
export const toServiceInfo = (svc: DisplayService): ServiceInfo => {
  const occupancyStatus =
    (svc as DisplayService & { occupancyStatus?: string }).occupancyStatus ??
    null;
  return {
    shoppingCartSlug: svc.shoppingCartSlug,
    quantity: svc.selectedQuantity,
    occupancyStatus,
    name: svc.name,
    price: svc.selectedPrice,
    totalInitialPrice: initialPriceForService(svc, svc.selectedQuantity),
    onlineImage: svc.onlineImage,
    slug: svc.connectSlug ?? undefined,
  };
};

/**
 * Convert a product-service catalog entry (`ProductService`) into the
 * cart-line `ServiceInfo` shape used by asset cart items. The asset
 * edit/upgrade flow ships one cart line per asset at the catalog default
 * unit price; quantity is fixed at 1.
 *
 * Bridges the cart-service ↔ product-service concept boundary at the cart
 * write site (use-asset-cart) — replaces a structural cast that papered
 * over the type mismatch between `ProductService` (catalog) and
 * `ServiceInfo` (cart line).
 */
export const productServiceToServiceInfo = (
  svc: ProductService,
): ServiceInfo => ({
  shoppingCartSlug: svc.shoppingCartSlug,
  quantity: 1,
  occupancyStatus: null,
  name: svc.name,
  price: svc.defaultPricing?.unitPrice ?? 0,
  totalInitialPrice: svc.defaultPricing?.unitPrice ?? 0,
  onlineImage: svc.onlineImage,
  slug: svc.connectSlug ?? undefined,
});

// ---------------------------------------------------------------------------
// Strip local-only fields before sending to API
// ---------------------------------------------------------------------------

// Services in the cart can carry mid-wizard `DisplayService` shapes
// (selectedQuantity / defaultQuantity) before `toServiceInfo` normalises
// them. The shape used by `normaliseServiceQuantity` covers both worlds.
type ServiceQuantitySource = {
  selectedQuantity?: number;
  quantity?: number | null;
  defaultQuantity?: number | null;
};

export const stripLocalFields = (
  item: PropertyCartItem
): Omit<PropertyCartItem, 'formattedAddress'> => {
  const { formattedAddress: _formattedAddress, ...rest } = item;

  // Normalise service quantity: the new DisplayService uses `selectedQuantity`
  // but the cart-service backend reads `quantity` for pricing lookups.
  if (rest.services) {
    rest.services = rest.services.map((svc) => {
      const source = svc as ServiceInfo & ServiceQuantitySource;
      return {
        ...svc,
        quantity:
          source.selectedQuantity ??
          source.quantity ??
          source.defaultQuantity ??
          1,
      };
    });
  }

  return rest;
};

// ---------------------------------------------------------------------------
// Price calculation helpers (pure functions)
// ---------------------------------------------------------------------------

export const calcSubtotal = (items: { price?: number | null }[]): number =>
  items.reduce((sum, item) => sum + (item.price || 0), 0);

export const calcPropertyInitialPrice = (
  items: PropertyCartItem[]
): number =>
  items.reduce((sum, item) => {
    if (item.services && item.services.length > 0) {
      return (
        sum +
        item.services.reduce(
          (s, svc) => s + (svc.totalInitialPrice || svc.price || 0),
          0
        )
      );
    }
    return sum;
  }, 0);

export const calcAssetInitialPrice = (items: AssetCartItem[]): number =>
  items.reduce(
    (sum, item) => sum + (item.service?.totalInitialPrice || item.price || 0),
    0
  );

// ---------------------------------------------------------------------------
// Entity grouping (pure function)
// ---------------------------------------------------------------------------

export const groupByEntity = <T extends { entityUUID?: string; entityName?: string; price?: number | null }>(
  items: T[]
): EntityGroup<T>[] => {
  const grouped = items.reduce<Record<string, EntityGroup<T>>>((acc, item) => {
    const key = item.entityUUID || 'default';
    if (!acc[key]) {
      acc[key] = {
        entityUUID: item.entityUUID || '',
        entityName: item.entityName || '',
        items: [],
        subtotal: 0,
      };
    }
    acc[key].items.push(item);
    acc[key].subtotal += item.price || 0;
    return acc;
  }, {});
  return Object.values(grouped);
};

export const hasMultipleEntityUUIDs = (
  propertyItems: { entityUUID?: string }[],
  assetItems: { entityUUID?: string }[]
): boolean => {
  const allEntityUUIDs = new Set([
    ...propertyItems.map((i) => i.entityUUID).filter(Boolean),
    ...assetItems.map((i) => i.entityUUID).filter(Boolean),
  ]);
  return allEntityUUIDs.size > 1;
};

// ---------------------------------------------------------------------------
// Bulk order cart data transforms (moved from cartService.tsx)
// ---------------------------------------------------------------------------

/**
 * Subset of CheckoutData consumed by the bulk-order transform — orderData
 * (property side) and editPhotoOrders (asset side) are the only fields read.
 */
type BulkOrderCartInput = {
  orderData?: PropertyCartItem[];
  editPhotoOrders?: AssetCartItem[];
};

/** Normalise quantity on services before sending to backend */
const normaliseServiceQuantity = (
  svc: ServiceInfo & ServiceQuantitySource,
) => ({
  ...svc,
  quantity:
    svc.selectedQuantity ?? svc.quantity ?? svc.defaultQuantity ?? 1,
});

export const transformBulkOrderCartData = (
  cartData: BulkOrderCartInput,
): Record<'orders', unknown> => {
  const formattedOrders: unknown[] = [];

  if (cartData?.orderData) {
    const propertyOrders = cartData.orderData.map((data) => ({
      firstAvailableDate: data.firstAvailableDate,
      propertyUUID: data.propertyId,
      orderedBy: localStorage.getItem('userName'),
      paymentMethod: {
        source: 'Credit Card',
        sourceTransactionId: '',
        sourcePaymentMethodId: '',
        sourceUserId: '',
        type: 'Card',
        amount: 0,
      },
      services: data.services?.map((svc) =>
        normaliseServiceQuantity(svc as ServiceInfo & ServiceQuantitySource),
      ),
      propertyAccess: data.propertyAccess,
      specialInstructions: data.specialInstructions,
      entityUUID: data.entityUUID,
      entityName: data.entityName,
    }));

    formattedOrders.push(...propertyOrders);
  }

  if (cartData?.editPhotoOrders) {
    const editPhotoOrders = cartData.editPhotoOrders.map((item) => ({
      propertyUUID: item.propertyUUID,
      photosetUUID: item.photosetUUID,
      propertyAddress: item.propertyAddress,
      services: [
        normaliseServiceQuantity(item.service as ServiceInfo & ServiceQuantitySource),
      ],
      assetRefs: item.assetRefs,
      sourceOrderUUID: item.sourceOrderUUID,
      assetNote: item.additionalNotes,
      // Stable cart-line id for post-checkout cleanup (DV-2131 §3). The dedicated
      // delete endpoint prefers this lineItemUUID; the (assetId, slug) composite
      // below is the legacy fallback for a line with no id. None of these are part
      // of the order-service contract — ignored server-side, consumed only by
      // PaymentPage cleanup.
      cartLineItemUUID: item.lineItemUUID,
      // Composite line identity (fallback): the singular assetId (not assetRefs[0],
      // which can diverge after a first-photo removal) + slug.
      cartLineAssetId: item.assetId,
      cartLineSlug: item.service?.shoppingCartSlug,
      origin: 'connect',
      orderedBy: localStorage.getItem('userName'),
      firstAvailableDate: '2025-02-10',
      // Per-order payment placeholder (empty; the real card is stamped onto the
      // top-level payment later). Distinct shape from util/orderPayment.buildOrderPayment
      // (no `tax`, card fields nested here) — kept inline on purpose; see the KNOWN HOLDOUT
      // note there if the payment wire shape changes (DV-2804).
      payment: {
        customerProfileId: '',
        customerPaymentProfileId: '',
        cardNumber: '',
        cardType: '',
      },
      entityUUID: item.entityUUID,
      entityName: item.entityName,
    }));

    formattedOrders.push(...editPhotoOrders);
  }

  return updatesKeyOfBulkOrderCartData('orders', formattedOrders);
};

export const updatesKeyOfBulkOrderCartData = <K extends string>(
  key: K,
  bulkOrderCartData: unknown,
): Record<K, unknown> => {
  return { [key]: bulkOrderCartData } as Record<K, unknown>;
};

// ---------------------------------------------------------------------------
// Asset cart display row derivation (DV-2131)
// ---------------------------------------------------------------------------

export type GroupedAssetRow = AssetCartItem & {
  quantity: number;
  assetIds: string[];
};

/**
 * Derive the display row for one grouped asset cart line. Under the DV-2131
 * grouped model each backend line already represents one service over N
 * photos, so quantity/assetIds come straight off the line's `assetRefs`
 * (with a singular-`assetId` fallback for confirmation rows that don't carry
 * refs).
 */
export const toGroupedRow = (item: AssetCartItem): GroupedAssetRow => {
  const assetIds = item.assetRefs?.length
    ? item.assetRefs.map((r) => r.assetId)
    : item.assetId
      ? [item.assetId]
      : [];
  const quantity = (item.service?.quantity ?? item.assetRefs?.length) || 1;
  return { ...item, quantity, assetIds };
};

/**
 * Cart-line mutating-state key for asset lines (DV-2131). Prefers the stable top-level
 * `lineItemUUID` (§3); falls back to the legacy `slug:assetId` composite for a line read from a
 * cart persisted before the field existed. MUST be used by both the writer (useCart) and the
 * reader (AssetCartTable) so they can never drift apart.
 */
export const assetLineKey = (
  lineItemUUID: string | undefined,
  assetId: string,
  slug: string,
): string => lineItemUUID ?? `${slug}:${assetId}`;

/**
 * Apply a cart asset-group edit to the single grouped line (DV-2131): drop the
 * removed photo refs and set the new note. Removing the last ref drops the
 * whole line from the returned array (note: the caller deletes the line via the
 * dedicated endpoint — see useAssetCartEdit). The line is matched by its stable
 * top-level `lineItemUUID` (§3) when present; otherwise by the legacy `assetId` +
 * `service.shoppingCartSlug` composite (the singular `assetId`, not `assetRefs[0]`,
 * because it stays stable after a first-photo removal).
 */
export const applyAssetGroupEdit = (
  items: AssetCartItem[],
  params: {
    lineItemUUID?: string;
    lineAssetId: string;
    lineSlug: string;
    removedRefIds: string[];
    note: string;
  },
): AssetCartItem[] => {
  const removed = new Set(params.removedRefIds);
  return items.flatMap((item) => {
    const matches = params.lineItemUUID
      ? item.lineItemUUID === params.lineItemUUID
      : (item.assetId ?? item.assetRefs?.[0]?.assetId) === params.lineAssetId &&
        item.service?.shoppingCartSlug === params.lineSlug;
    if (!matches) return [item];
    const nextRefs = (item.assetRefs ?? []).filter((r) => !removed.has(r.assetId));
    if (nextRefs.length === 0) return [];
    return [{
      ...item,
      assetRefs: nextRefs,
      additionalNotes: params.note,
      service: { ...item.service, quantity: nextRefs.length },
    }];
  });
};
